import PhoneIcon from "@atlaskit/icon/core/phone";
import {useMemo} from "react";
import {adaptTableRowActionsConfigForKanban} from "~/components/ottopilotDataViewer/rowEngagement/adaptTableRowActionsConfigForKanban";
import {TableActionsColumn} from "~/components/table/TableActionsColumn";
import type {TableRowActionsConfig} from "~/components/table/tableRowActions.types";
import {AnankeLinkIconButton} from "@vesta/ananke/components/anankeLinkIconButton";
import {AnankeTooltip} from "@vesta/ananke/components/anankeTooltip";

export type KanbanCardPhoneDial = {
    telHref: string;
    tooltip?: string;
    isDisabled?: boolean;
    onDial?: () => void;
};

/** Kanban header: call, edit, ⋮ (profile, dispose, view, create, notes, schedule, archive, …). */
export function KanbanCardRowActions<TRow>({
    row,
    config,
    phoneDial,
}: {
    row: TRow;
    config: TableRowActionsConfig<TRow> | undefined;
    phoneDial?: KanbanCardPhoneDial | null;
}) {
    const kanbanConfig = useMemo(() => adaptTableRowActionsConfigForKanban(config), [config]);

    if (kanbanConfig === undefined) {
        return phoneDial !== undefined ? (
            <div
                className="kanban-card-row-actions flex shrink-0 items-center gap-ds-space-075"
                onClick={(event) => {
                    event.stopPropagation();
                }}
                onPointerDown={(event) => {
                    event.stopPropagation();
                }}
            >
                <PhoneDialButton phoneDial={phoneDial} />
            </div>
        ) : null;
    }

    return (
        <div
            className="kanban-card-row-actions flex shrink-0 items-center gap-ds-space-075"
            onClick={(event) => {
                event.stopPropagation();
            }}
            onPointerDown={(event) => {
                event.stopPropagation();
            }}
        >
            {phoneDial !== undefined ? <PhoneDialButton phoneDial={phoneDial} /> : null}
            <TableActionsColumn row={row} config={kanbanConfig} />
        </div>
    );
}

function PhoneDialButton({phoneDial}: {phoneDial: KanbanCardPhoneDial | null}) {
    const button = (
        <AnankeLinkIconButton
            spacing="compact"
            icon={PhoneIcon}
            label="Call"
            appearance="subtle"
            href={phoneDial?.telHref ?? "tel:"}
            isDisabled={phoneDial === null || phoneDial.isDisabled === true}
            onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                if (phoneDial?.onDial !== undefined) {
                    phoneDial.onDial();
                }
            }}
        />
    );

    if (phoneDial?.tooltip !== undefined && phoneDial.tooltip !== "") {
        return (
            <AnankeTooltip content={phoneDial.tooltip} position="bottom">
                {(tooltipProps) => <span {...tooltipProps}>{button}</span>}
            </AnankeTooltip>
        );
    }

    return button;
}
